import { useEffect, useRef, useState } from 'react';
import { MOTION_DURATION_MS, easeOut } from '~styles/motion';
import { usePrefersReducedMotion } from '~styles/use-prefers-reduced-motion';

export interface UseTweenedValueOptions {
    durationMs?: number;
    reducedMotion?: boolean;
}

/** Tweens `value` toward its target on change; the first render shows it as
 * is. `Date.now` (not `performance.now`) drives elapsed time, so fake timers
 * in tests move it too. */
export const useTweenedValue = (
    value: number,
    options: UseTweenedValueOptions = {}
): number => {
    const systemReducedMotion = usePrefersReducedMotion();
    const reducedMotion = options.reducedMotion ?? systemReducedMotion;
    const durationMs = options.durationMs ?? MOTION_DURATION_MS;

    const [displayed, setDisplayed] = useState(value);
    const displayedRef = useRef(value);

    useEffect(() => {
        if (displayedRef.current === value) return undefined;

        if (
            reducedMotion ||
            !Number.isFinite(value) ||
            !Number.isFinite(displayedRef.current)
        ) {
            displayedRef.current = value;
            setDisplayed(value);
            return undefined;
        }

        const from = displayedRef.current;
        const to = value;
        const start = Date.now();
        let frame: number;

        const tick = () => {
            const t = Math.min(1, (Date.now() - start) / durationMs);
            const next = t >= 1 ? to : from + (to - from) * easeOut(t);
            displayedRef.current = next;
            setDisplayed(next);
            if (t < 1) {
                frame = requestAnimationFrame(tick);
            }
        };

        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [value, reducedMotion, durationMs]);

    // The snap above lands one commit late; without this that commit paints NaN.
    return Number.isFinite(displayed) ? displayed : value;
};

export type UseTweenedValuesOptions = UseTweenedValueOptions;

export interface UseTweenedValuesResult {
    values: number[];
    /** True once every element has landed on its target (including a fresh
     * mount, or a change too small for the hook to have started a tween). */
    settled: boolean;
}

/** Array counterpart of `useTweenedValue`: one rAF loop tweens every element
 * toward its own target. A non-finite element snaps on its own; so does the
 * whole array on a length change, having nothing to interpolate against. */
export const useTweenedValues = (
    values: number[],
    options: UseTweenedValuesOptions = {}
): UseTweenedValuesResult => {
    const systemReducedMotion = usePrefersReducedMotion();
    const reducedMotion = options.reducedMotion ?? systemReducedMotion;
    const durationMs = options.durationMs ?? MOTION_DURATION_MS;

    const [displayed, setDisplayed] = useState(values);
    const displayedRef = useRef(values);
    // Keyed on content, not the array reference: a caller building `values`
    // inline hands a new array on every tick-driven re-render too, which
    // would otherwise restart the tween from `Date.now()` each frame.
    const signature = values.join(',');

    useEffect(() => {
        const previous = displayedRef.current;
        const sameLength = previous.length === values.length;
        const unchanged =
            sameLength && previous.every((entry, i) => entry === values[i]);
        if (unchanged) return undefined;

        if (!sameLength || reducedMotion) {
            displayedRef.current = values;
            setDisplayed(values);
            return undefined;
        }

        const from = previous;
        const to = values;
        const start = Date.now();
        let frame: number;

        const tick = () => {
            const t = Math.min(1, (Date.now() - start) / durationMs);
            const eased = easeOut(t);
            const next = to.map((target, i) => {
                const source = from[i];
                if (!Number.isFinite(target) || !Number.isFinite(source)) {
                    return target;
                }
                return t >= 1 ? target : source + (target - source) * eased;
            });
            displayedRef.current = next;
            setDisplayed(next);
            if (t < 1) {
                frame = requestAnimationFrame(tick);
            }
        };

        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature, reducedMotion, durationMs]);

    const settled =
        displayed.length === values.length &&
        // `Object.is`, not `===`: a NaN target (a column with no rate) is
        // handed straight through, and would otherwise never count as landed.
        displayed.every((entry, i) => Object.is(entry, values[i]));

    // A length change snaps on the effect, one render after the caller already
    // asked for the new shape; hand back the targets so that render never
    // indexes past the end of the old array.
    return {
        values:
            displayed.length === values.length
                ? displayed.map((entry, i) =>
                      Number.isFinite(entry) ? entry : values[i]
                  )
                : values,
        settled,
    };
};
